/* eslint-disable functional/immutable-data */

import type { Cache } from '@urql/exchange-graphcache';

import type {
  GetPaymentSubscriptionProfileQuery,
  MutationRemoveSubscriptionPaymentMethodArgs,
  MutationUpdateSubscriptionPaymentMethodArgs,
  PaymentSubscriptionProfile,
  RemoveSubscriptionPaymentMethodMutation,
  SubscriptionCreditCard,
  UpdateSubscriptionPaymentMethodMutation,
} from '@order/graphql';
import { GetPaymentSubscriptionProfileDocument } from '@order/graphql';
import { GetSweetpassMembershipDocument } from '@order/graphql';

/**
 * Changing addresses returns an address object.
 * This is fine for when the address is updated,
 * But if it is added or removed, this is needed to update
 * the current customer's object's addresses in URQL cache.
 */

export const updateSubscriptionPaymentMethodCacheUpdate = (
  result: UpdateSubscriptionPaymentMethodMutation,
  input: MutationUpdateSubscriptionPaymentMethodArgs,
  cache: Cache,
) => {
  const typename = result.updateSubscriptionPaymentMethod.__typename;

  // Ignore unsuccessful mutations.
  if (typename !== 'UpdateSubscriptionPaymentMethodSuccessResponse') return;

  updateCacheWithCreditCardChanges(cache, {
    paymentMethodId: input.paymentMethodId,
  });
};

export const removeSubscriptionPaymentMethodCacheUpdate = (
  result: RemoveSubscriptionPaymentMethodMutation,
  input: MutationRemoveSubscriptionPaymentMethodArgs,
  cache: Cache,
) => {
  const typename = result.removeSubscriptionPaymentMethod.__typename;

  // Ignore unsuccessful mutations.
  if (typename !== 'DeleteSubscriptionPaymentMethodSuccess') return;

  updateCacheWithCreditCardChanges(cache, {
    paymentMethodId: input.paymentMethodId,
    removed: true,
  });
};

const updateCacheWithCreditCardChanges = (
  cache: Cache,
  creditCardChanges: CreditCardChanges,
) => {
  cache.updateQuery({ query: GetSweetpassMembershipDocument }, (data) => {
    if (!data) return data;

    // Get cached credit cards.
    const cachedPaymentSubscriptionProfile =
      data?.paymentSubscriptionProfile as PaymentSubscriptionProfile;
    const creditCards = cachedPaymentSubscriptionProfile?.creditCards ?? [];

    // If the credit card was removed, remove it from the cache.
    if (creditCardChanges.removed) {
      const withoutUpdated = creditCards.filter(
        ({ id }) => id !== creditCardChanges.paymentMethodId,
      );

      data.paymentSubscriptionProfile = {
        ...cachedPaymentSubscriptionProfile,
        creditCards: withoutUpdated,
      };

      return data;
    }

    // If the credit card was added, add it and sort by id.
    data.paymentSubscriptionProfile = {
      ...cachedPaymentSubscriptionProfile,
      creditCards: creditCards.map((creditCard) => ({
        ...creditCard,
        isActive: creditCardChanges.paymentMethodId === creditCard.id,
      })),
    };

    return data;
  });
};

export function updatePaymentSubscriptionProfile(
  cache: Cache,
  creditCard: SubscriptionCreditCard,
) {
  cache.updateQuery<GetPaymentSubscriptionProfileQuery>(
    { query: GetPaymentSubscriptionProfileDocument },
    (data) => {
      if (!data?.paymentSubscriptionProfile) return data;

      return {
        ...data,
        paymentSubscriptionProfile: {
          ...data.paymentSubscriptionProfile,
          creditCards: [
            creditCard,
            ...(data.paymentSubscriptionProfile.creditCards ?? []),
          ],
        },
      };
    },
  );
}

type CreditCardChanges = {
  readonly paymentMethodId: string;
  readonly removed?: boolean;
};
